import axios, { AxiosResponse } from 'axios';
import moment from 'moment';

import { FEEDBACK_MONTHS_LOOKBACK } from '../constants/feedbackConfiguration';
import { PRODUCTIVITY_HOURS_LOOKBACK } from '../constants/productivityConfiguration';
import { REQUEST_HELP_MONTHS_LOOKACK } from '../constants/requestHelpConfiguration';
import {
  CreateRequestHelpActionOutput,
  GetEmployeeDetailsOutput,
  GetExpectedDPMOChangeEventsOutput,
  GetFilteredFeedbackHistoryOutput,
  GetProductivityDataOutput,
  GetProductivityReportOutput,
  GetQualityEventsOutput,
  GetQualityReportOutput,
  GetRequestHelpActionsOutput,
  GetSQPRQualityErrorsOutput,
  GetUnknownIdleTimeMetricsOutput,
  GetWeeklySpprSummariesOutput,
  GetWeeklySqprSummariesOutput,
  HelpType,
  SendEmailReportOutput,
  GetAuditReportOutput,
} from '../types/backendService';
import {getURL, isAftxURL} from "src/constants/stageConfiguration";

interface PostOptions {
  allowedStatuses: Array<number>; // statuses that will not cause the promise to be rejected
}

const defaultOptions = {
  allowedStatuses: [200],
};

const getHeaders = (userToken: string) => {
  const headers: Record<string, string> = {
    // eslint-disable-next-line quote-props
    'Content-Type': 'application/json',
  };

  if (!isAftxURL()) {
    headers['Authorization'] = `${userToken}`;
  }

  return headers;
};

export class BackendServiceClient {
  // eslint-disable-next-line class-methods-use-this
  public async post(
    path: string,
    headers: Record<string, string>,
    body: Record<string, any>,
    { allowedStatuses }: PostOptions = defaultOptions,
  ): Promise<AxiosResponse<any>> {
    const requestURL = getURL();
    const baseUrl: string = `${requestURL}api`; // The URL already includes a '/'

    const response = await axios.post(baseUrl + path, body, {
      headers,
      validateStatus(status) {
        return [...(allowedStatuses || []), 200].includes(status);
      },
      withCredentials: true
    });

    return response;
  }

  public async getEmployeeDetails(
    userToken: string,
    employeeLogin: string,
  ): Promise<GetEmployeeDetailsOutput> {
    const headers = getHeaders(userToken);

    const body = {
      login: employeeLogin,
    };

    const response = await this.post('/getEmployeeDetails', headers, body);

    return response.data;
  }

  public async getFilteredFeedbackHistory(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    feedbackCategories?: string[],
  ): Promise<GetFilteredFeedbackHistoryOutput> {
    const headers = getHeaders(userToken);

    const currentMoment = moment();
    const startTime = currentMoment
      .startOf('day')
      .subtract(FEEDBACK_MONTHS_LOOKBACK, 'months')
      .subtract(1, 'days');

    const body = {
      employeeId,
      login: employeeLogin,
      feedbackCategories,
      startTime: startTime.unix(),
    };

    const response = await this.post('/getFilteredFeedbackHistory', headers, body);

    return response.data;
  }

  public async getHourlyProductivityData(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
  ): Promise<GetProductivityDataOutput> {
    const headers = getHeaders(userToken);

    const currentMoment = moment();
    const endTime = currentMoment.startOf('hour').clone();
    const startTime = endTime.clone().subtract(PRODUCTIVITY_HOURS_LOOKBACK, 'hours');

    const body = {
      employeeId,
      login: employeeLogin,
      startTime: startTime.unix(),
      endTime: endTime.unix(),
      granularity: 'Hourly',
    };

    const response = await this.post('/getProductivityData', headers, body);

    return response.data;
  }

  public async getDailyProductivityDataForWeek(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment,
  ): Promise<GetProductivityDataOutput> {
    const headers = getHeaders(userToken);

    const weekStart = weekEnd.clone().subtract(7, 'days');

    const body = {
      employeeId,
      login: employeeLogin,
      startTime: weekStart.unix(),
      endTime: weekEnd.unix(),
      granularity: 'Daily',
    };

    const response = await this.post('/getProductivityData', headers, body);

    return response.data;
  }

  public async getWeeklySpprSummaries(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
  ): Promise<GetWeeklySpprSummariesOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
    };

    const response = await this.post('/getWeeklySpprSummaries', headers, body);

    return response.data;
  }

  public async getQualityEvents(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment,
  ): Promise<GetQualityEventsOutput> {
    const headers = getHeaders(userToken);

    const weekStart = weekEnd.clone().subtract(7, 'days');

    const body = {
      employeeId,
      login: employeeLogin,
      startTime: weekStart.unix(),
      endTime: weekEnd.unix(),
    };

    const response = await this.post('/getQualityEvents', headers, body);

    return response.data;
  }

  public async getWeeklySqprSummaries(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
  ): Promise<GetWeeklySqprSummariesOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
    };

    const response = await this.post('/getWeeklySqprSummaries', headers, body);

    return response.data;
  }

  public async getSQPRQualityErrors(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment,
  ): Promise<GetSQPRQualityErrorsOutput> {
    const headers = getHeaders(userToken);

    const weekStart = weekEnd.clone().subtract(7, 'days');

    const body = {
      employeeId,
      login: employeeLogin,
      startTime: weekStart.unix(),
      endTime: weekEnd.unix(),
    };

    const response = await this.post('/getSQPRQualityErrors', headers, body);

    return response.data;
  }

  public async createRequestHelpAction(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string,
    managerLogin: string,
    helpType: HelpType,
    actionDetails: Record<string, string> | undefined,
    supplementalUrls: Record<string, string> | undefined,
  ): Promise<CreateRequestHelpActionOutput> {
    const headers = getHeaders(userToken);

    const body = {
      requestHelpAction: {
        login: employeeLogin,
        employeeId,
        warehouseId,
        assignedTo: managerLogin,
        helpType,
        actionDetails,
        supplementalUrls,
      },
    };

    const response = await this.post('/createRequestHelpAction', headers, body);

    return response.data;
  }

  public async getRequestHelpActions(
    userToken: string,
    employeeLogin: string,
  ): Promise<GetRequestHelpActionsOutput> {
    const headers = getHeaders(userToken);

    const currentMoment = moment();
    const endTime = currentMoment.clone();
    const startTime = endTime.clone().subtract(REQUEST_HELP_MONTHS_LOOKACK, 'months');

    const body = {
      employeeLogin,
      startTime: startTime.unix(),
      endTime: endTime.unix(),
    };

    const response = await this.post('/getRequestHelpActions', headers, body);

    return response.data;
  }

  public async getProductivityReport(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string,
  ): Promise<GetProductivityReportOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
      warehouseId,
    };

    const response = await this.post('/getProductivityReport', headers, body);

    return response.data;
  }

  public async getQualityReport(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string,
  ): Promise<GetQualityReportOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
      warehouseId,
    };

    const response = await this.post('/getQualityReport', headers, body);

    return response.data;
  }

  public async getExpectedDPMOChangeEvents(
    userToken: string,
    _employeeId: string,
    employeeLogin: string,
  ): Promise<GetExpectedDPMOChangeEventsOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeLogin,
    };

    const response = await this.post('/getExpectedDPMOChangeEvents', headers, body, {
      allowedStatuses: [200, 401],
    });

    /**
     * In current state, expected DPMO will attempt to be pulled only if the user
     * selected an approved warehouse in FC Menu.
     * However, the backend will emit an Unauthorized 401 exception if the user's home warehouse in PAPI is not approved
     * for expected DPMO. In such an event we simply catch the error and display no data to the user.
     *
     * Longer term we should likely use PAPI home warehouse for all validations.
     */
    if (response.status === 401) {
      return Promise.resolve({
        expectedDPMOChangeEvents: [],
      });
    }

    return response.data;
  }

  public async sendEmailReport(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    reportType: string,
    warehouseId: string,
  ): Promise<SendEmailReportOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
      reportType,
      warehouseId,
    };

    const response = await this.post('/SendEmailReport', headers, body);

    return response.data;
  }

  public async getUnknownIdleTimeMetrics(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string,
  ): Promise<GetUnknownIdleTimeMetricsOutput> {
    const headers = getHeaders(userToken);

    const body = {
      employeeId,
      login: employeeLogin,
      warehouseId,
    };

    const response = await this.post('/GetUnknownIdleTimeMetrics', headers, body);
    return response.data;
  }

  public async getAuditReport(
    userToken: string,
    login: string,
    employeeId: string,
  ): Promise<GetAuditReportOutput> {
    const headers = getHeaders(userToken);

    const body = {
      login: login,
      employeeId: employeeId,
    };

    const response = await this.post('/getAuditReport', headers, body);
    return response.data;

  }
}
